/**
 * Navigation icons, transcribed from the design canvas (1a, "Navegação mobile").
 * Each keeps its original viewBox — they come from different sources and re-fitting
 * them to a common grid would distort the line weights the canvas balanced.
 *
 * All are decorative: the nav renders a visible text label next to every one, so they
 * carry aria-hidden and contribute no accessible name.
 */

interface IconProps {
  size?: number;
  className?: string;
}

const base = (size: number) => ({
  width: size,
  height: size,
  display: 'block' as const,
});

/**
 * Cartões empilhados — Timeline. Desenhado aqui, e não trazido de fora como os outros: o
 * canvas descreve o feed como "cada volta é um cartão, não uma linha de fluxo", e o ícone
 * repete essa forma. Traço de 1,6 px, como o ChartIcon, para pesar igual na barra.
 */
export function TimelineIcon({ size = 20, className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={base(size)}
      className={className}
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3.8"
        y="4"
        width="16.4"
        height="6.4"
        rx="1.6"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <rect
        x="3.8"
        y="13.6"
        width="16.4"
        height="6.4"
        rx="1.6"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="M7 7.2h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M7 16.8h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/** A golf club and ball — Início. */
export function ClubIcon({ size = 20, className }: IconProps) {
  return (
    <svg viewBox="0 0 15 15" style={base(size)} className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M3.3999,1.1c0,0.1,0,0.2,0,0.2c0,0.4,0.3,0.7,0.7,0.7c0.3,0,0.5-0.2,0.6-0.5l0,0L4.9,1l5.6,2.3L6.6,6C6.2,6.3,6.2,6.7,6.3,7.1l0.9,2.1l-1.3,3.9C5.7,13.6,6.1,14,6.5,14c0.3,0,0.5-0.1,0.6-0.5l1.4-4l0.1,0.3v3.5c0,0,0,0.7,0.7,0.7s0.7-0.7,0.7-0.7V10c0-0.2,0-0.3-0.1-0.5L8.5,6.1l2.7-1.9c0.2-0.2,0.4-0.3,0.4-0.6s-0.2-0.5-0.4-0.6L4,0.1c-0.0878,0-0.118,0.0179-0.2001,0.1L3.3999,1.1z M5.5,3C4.7,3,4,3.7,4,4.5S4.7,6,5.5,6S7,5.3,7,4.5S6.2999,3,5.5,3z"
      />
    </svg>
  );
}

/** A golf cart — Voltas. */
export function CartIcon({ size = 21, className }: IconProps) {
  return (
    <svg viewBox="0 0 50 50" style={base(size)} className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M23 7C14.523438 7 8.8125 8 8.8125 8C8.261719 8.101563 7.894531 8.636719 8 9.1875C8.101563 9.738281 8.636719 10.105469 9.1875 10C9.1875 10 9.945313 9.890625 11 9.75L11 18L6 18L6 20L11 20L11 25.21875C9.96875 25.117188 8.945313 25.0625 7.96875 25.09375C6.492188 25.140625 5.179688 25.371094 4.28125 26.3125C3.199219 27.445313 2.683594 29.058594 2.375 30.78125C2.066406 32.503906 2 34.355469 2 36C2 36.550781 2.449219 37 3 37L4.09375 37C4.027344 37.324219 4 37.65625 4 38C4 40.75 6.25 43 9 43C11.40625 43 13.4375 41.273438 13.90625 39L38.09375 39C38.5625 41.273438 40.59375 43 43 43C45.75 43 48 40.75 48 38C48 37.785156 47.964844 37.582031 47.9375 37.375C47.980469 37.253906 48.003906 37.128906 48 37L48 31C48 30.914063 47.988281 30.832031 47.96875 30.75C47.40625 28.503906 45.164063 27.042969 43.1875 25.90625C41.542969 24.964844 40.230469 24.410156 39.75 24.21875L33.3125 9.53125C35.503906 9.769531 36.8125 10 36.8125 10C37.363281 10.105469 37.898438 9.738281 38 9.1875C38.105469 8.636719 37.738281 8.101563 37.1875 8C37.1875 8 31.476563 7 23 7 Z M 23 9C26.136719 9 28.871094 9.136719 31.0625 9.3125C31.070313 9.34375 31.082031 9.375 31.09375 9.40625L37.90625 25L37.46875 26L30.4375 19L31.71875 17.71875C32.042969 17.417969 32.128906 16.941406 31.933594 16.546875C31.742188 16.148438 31.308594 15.929688 30.875 16C30.652344 16.023438 30.441406 16.125 30.28125 16.28125L28.5 18.09375C28.316406 18.179688 28.164063 18.324219 28.0625 18.5L26.28125 20.28125C25.882813 20.679688 25.882813 21.320313 26.28125 21.71875C26.679688 22.117188 27.320313 22.117188 27.71875 21.71875L29 20.4375L36.59375 27.96875L34.34375 33L26 33L26 31.28125C26 30.0625 25.128906 29.113281 24.125 28.71875C22.207031 27.96875 20.101563 27.242188 17.9375 26.625C17.890625 26.46875 16.847656 23.078125 15.9375 20.65625C15.316406 19.003906 13.957031 18.363281 13 18.125L13 9.5C15.410156 9.25 18.855469 9 23 9 Z M 13 20.28125C13.394531 20.460938 13.820313 20.699219 14.0625 21.34375C14.679688 22.980469 15.25 24.792969 15.625 26C14.746094 25.792969 13.871094 25.644531 13 25.5 Z M 39.5 26.34375C40.058594 26.570313 40.804688 26.832031 42.1875 27.625C43.984375 28.65625 45.710938 30.175781 46 31.21875L46 34.03125C45.160156 33.390625 44.132813 33 43 33C40.59375 33 38.5625 34.726563 38.09375 37L13.90625 37C13.4375 34.726563 11.40625 33 9 33C7.375 33 5.914063 33.789063 5 35L4.0625 35C4.09375 33.699219 4.125 32.34375 4.34375 31.125C4.617188 29.59375 5.117188 28.316406 5.71875 27.6875C5.96875 27.425781 6.789063 27.132813 8.03125 27.09375C12.601563 26.953125 18.636719 28.695313 23.40625 30.5625C23.816406 30.722656 24 30.984375 24 31.28125L24 34C24 34.550781 24.449219 35 25 35L35 35C35.390625 34.996094 35.746094 34.765625 35.90625 34.40625 Z M 9 35C10.667969 35 12 36.332031 12 38C12 39.667969 10.667969 41 9 41C7.332031 41 6 39.667969 6 38C6 36.332031 7.332031 35 9 35 Z M 43 35C44.667969 35 46 36.332031 46 38C46 39.667969 44.667969 41 43 41C41.332031 41 40 39.667969 40 38C40 36.332031 41.332031 35 43 35Z"
      />
    </svg>
  );
}

/**
 * A golf ball on a tee — Registrar. This is the one icon drawn as strokes rather than a
 * filled shape, because it sits inside the gold FAB circle and needs to read as a cutout.
 */
export function BallOnTeeIcon({ size = 18, className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={base(size)}
      className={className}
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="5.6" stroke="currentColor" strokeWidth="1.8" fill="none" />
      <circle cx="10" cy="6.6" r=".55" fill="currentColor" />
      <circle cx="12.2" cy="6" r=".55" fill="currentColor" />
      <circle cx="14" cy="7.4" r=".55" fill="currentColor" />
      <circle cx="10.6" cy="9" r=".55" fill="currentColor" />
      <circle cx="13" cy="9.6" r=".55" fill="currentColor" />
      <path
        d="M9.5 14.3 11.5 21.5h1l2-7.2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** A rising bar chart — Estatísticas. */
export function ChartIcon({ size = 19, className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={base(size)}
      className={className}
      fill="none"
      aria-hidden="true"
    >
      <path d="M4 20.2h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path
        d="M7.4 20.2v-5.4M12 20.2v-10.4M16.6 20.2v-7.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** A tree with a pin — Campos. */
export function TreeIcon({ size = 20, className }: IconProps) {
  return (
    <svg viewBox="0 0 512 512" style={base(size)} className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M260.567,216.439c15.443,0,27.973-5.8,27.973-12.999c0-5.946-8.612-10.889-20.304-12.45V76.291l79.235-34.768L252.898,0v77.043v5.978V190.99c-11.701,1.553-20.296,6.504-20.296,12.45C232.602,210.639,245.099,216.439,260.567,216.439z"
      />
      <path
        fill="currentColor"
        d="M426.107,190.634c-13.274-18.152-34.323-33.32-60.395-44.961c-21.226-9.44-45.899-16.413-72.626-20.223v21.801c33.838,5.193,63.671,15.863,85.545,29.502c13.113,8.146,23.346,17.302,30.068,26.557c6.747,9.295,9.982,18.46,9.991,27.432c0,1.01-0.041,2.03-0.129,3.066c-0.275,3.543-1.78,8.202-4.668,13.695c-4.303,8.243-11.568,18.12-20.182,29.154c-8.551,10.977-18.42,23.16-28.006,36.653c-17.311,20.668-29.089,44.953-37.138,65.702c-4.045,10.443-7.135,19.996-9.424,27.73c-2.288,7.701-3.818,13.695-4.619,16.567c-5.897,21.009-17.254,37.414-33.57,48.949c-16.333,11.495-37.956,18.193-64.812,18.201c-8.599,0-17.724-0.688-27.342-2.144c-29.332-4.401-53.835-14.238-70.345-27.213c-8.275-6.488-14.561-13.695-18.751-21.364c-4.19-7.684-6.374-15.815-6.383-24.705c0-2.434,0.162-4.918,0.51-7.458c0.955-7.053,3.721-13.987,7.758-21.089c6.035-10.654,14.884-21.356,22.925-32.746c4.013-5.719,7.83-11.649,10.872-18.056c2.936-6.18,5.129-12.87,5.84-20.078c1.19-4.562,1.812-9.06,1.812-13.388c0-8.988-2.322-17.126-5.396-24.487c-4.651-11.066-10.872-20.79-15.621-30.894c-4.78-10.12-8.194-20.328-8.218-32.705c0-1.812,0.08-3.673,0.243-5.59c0.712-8.397,4.174-16.309,10.621-24.002c9.626-11.495,26.185-21.946,47.613-29.243c16.228-5.549,35.189-9.311,55.769-10.815v-21.526c-32.633,2.201-62.029,9.61-85.278,21.421c-13.962,7.126-25.773,15.854-34.574,26.29c-8.784,10.395-14.52,22.707-15.62,36.087c-0.21,2.507-0.308,4.967-0.308,7.377c-0.007,22.108,8.494,39.104,15.888,52.92c3.729,6.933,7.208,13.186,9.602,18.962c2.411,5.792,3.738,10.962,3.738,16.204c0,2.791-0.364,5.646-1.262,8.801l-0.283,1.003l-0.089,1.043c-0.453,5.655-2.702,11.641-6.455,18.185c-5.573,9.821-14.391,20.426-22.65,32.228c-8.194,11.811-16.049,25.126-18.265,41.199c-0.478,3.487-0.712,6.941-0.712,10.354c-0.008,12.514,3.203,24.422,9.028,35.044c8.744,15.96,23.094,29,41.029,38.958c17.959,9.966,39.622,16.931,63.752,20.58c10.604,1.594,20.798,2.386,30.554,2.386c30.407,0.008,56.714-7.685,77.212-22.141c20.523-14.415,34.914-35.529,41.911-60.775c0.922-3.317,2.338-8.866,4.522-16.211c6.464-21.955,19.851-59.142,42.712-86.079l0.291-0.348l0.267-0.38c12.199-17.222,25.361-32.754,36.079-47.242c5.364-7.256,10.12-14.261,13.832-21.323c3.681-7.054,6.399-14.237,7.063-21.914c0.129-1.626,0.194-3.235,0.194-4.845C440.231,216.398,434.925,202.712,426.107,190.634z"
      />
    </svg>
  );
}
